import { Box, Grid, GridItem, VStack } from '@web/components/atoms/Layout'
import { Body2, Text } from '@web/components/atoms/Typography'
import { useEffect, useMemo, useState } from 'react'
import { CountdownTimer as CountdownTimerProps } from '@web/graphql-components'
import { getCountdownTimerDefaultStyles } from './countdownTimerUtils'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import React from 'react'
import { differenceInDays, intervalToDuration } from 'date-fns'
import { useIsHiddenFromDates, useLocalDates } from '@web/components/utils/dateUtils'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { CountdownTimerVariation } from '@web/graphql-types'

type TimeSlotProps = {
  name: string
  number: number
  labelColor?: string
  timerColor?: string
}

const CountdownTimerText = ({ color, children }: React.PropsWithChildren<{ color?: string }>) => (
  <Text color={color} fontWeight="600">
    {children}
  </Text>
)

const TimeSlot = ({ name, number, labelColor, timerColor }: TimeSlotProps) => (
  <VStack gap="0" whiteSpace="nowrap">
    <Body2 fontSize="0.4em" color={labelColor}>
      {name}
    </Body2>
    <CountdownTimerText color={timerColor}>{number >= 10 ? number : `0${number}`}</CountdownTimerText>
  </VStack>
)

const TIMER_SLOT_NAME = ['Days', 'Hours', 'Minutes', 'Seconds']

// temporarily
const CountdownTimer = ({
  noTimer,
  styles,
  mobileStyles,
  start,
  end,
  dateToUse,
  ...props
}: CountdownTimerProps & { noTimer?: boolean; dateToUse?: string | null }) => {
  const colorData = useBrandingsStore(({ colorData }) => colorData)
  const defaultStyles = useMemo(() => getCountdownTimerDefaultStyles(colorData), [colorData])
  const finishTimeToUse = useMemo(() => {
    if (!props.bonusFinishTimes || props.bonusFinishTimes.length === 0) {
      return props.finish
    }

    const current = dateToUse ? new Date(dateToUse) : new Date()
    const validFinishTimes = [props.finish, ...props.bonusFinishTimes].filter((time) => new Date(time) > current)

    return validFinishTimes.length > 0 ? validFinishTimes[0] : props.finish
  }, [props.finish, props.bonusFinishTimes, dateToUse])
  const [finish, currentDate] = useLocalDates([finishTimeToUse, dateToUse])
  const isHidden = useIsHiddenFromDates(start, end ? finishTimeToUse : '', dateToUse)
  const variant = styles?.variant || CountdownTimerVariation.Full
  const timeSlots = styles?.timeSlots || ['days', 'hours', 'minutes', 'seconds']

  const { borderRadius = defaultStyles.borderRadius } = styles || {}
  const backgroundColor = useColorWithOpacity(
    styles?.backgroundColor,
    styles?.backgroundColorOpacity,
    defaultStyles.backgroundColor,
  )
  const labelColor = useColorWithOpacity(styles?.labelColor, styles?.labelColorOpacity, defaultStyles.labelColor)
  const timerColor = useColorWithOpacity(styles?.timerColor, styles?.timerColorOpacity, defaultStyles.timerColor)

  const [timer, setTimer] = useState<number[]>([0, 0, 0, 0])

  useEffect(() => {
    if (noTimer || !finish) {
      return
    }

    const calculateTimeLeft = () => {
      const {
        hours = 0,
        minutes = 0,
        seconds = 0,
      } = intervalToDuration({
        start: currentDate ?? new Date(),
        end: finish,
      })
      const days = differenceInDays(currentDate ?? new Date(), finish) * -1

      if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
        setTimer([0, 0, 0, 0])

        return true
      }

      setTimer([days, hours, minutes, seconds])

      return false
    }

    const timerInterval = setInterval(() => {
      const shouldStop = calculateTimeLeft()

      if (shouldStop) {
        clearInterval(timerInterval)
      }
    }, 1000)

    calculateTimeLeft()

    return () => {
      clearInterval(timerInterval)
    }
  }, [finish])

  if (isHidden) {
    return null
  }

  return (
    <Box
      textAlign="center"
      w="100%"
      bgColor={backgroundColor}
      borderRadius={`${borderRadius}px`}
      className="c-component-wrapper"
      css={{
        containerType: 'inline-size',
        containerName: 'timer',
        fontSize: '3.75rem',
        '@container page (min-width: 0px)': {
          padding: mobileStyles?.padding,
          fontSize: '2.75rem',
        },
        '@container page (min-width: 901px)': {
          padding: styles?.padding,
          fontSize: '3.75rem',
        },
      }}
    >
      {variant === CountdownTimerVariation.Full && (
        <>
          <Grid
            templateColumns={`repeat(${timer.length - 1}, 1fr 0.1fr) 1fr`}
            alignItems="end"
            w="100%"
            css={{
              '@container timer (min-width: 0px)': {
                display: 'none',
              },
              '@container timer (min-width: 270px)': {
                display: 'grid',
              },
            }}
          >
            {timer.map((t, index) => (
              <React.Fragment key={`ct-slot-${TIMER_SLOT_NAME[index]}`}>
                {index !== 0 && (
                  <GridItem>
                    <CountdownTimerText color={timerColor}>:</CountdownTimerText>
                  </GridItem>
                )}
                <GridItem>
                  <TimeSlot
                    key={index}
                    name={TIMER_SLOT_NAME[index]}
                    number={t}
                    labelColor={labelColor}
                    timerColor={timerColor}
                  />
                </GridItem>
              </React.Fragment>
            ))}
          </Grid>
          <Grid
            templateColumns="1fr 0.1fr 1fr"
            alignItems="end"
            w="100%"
            css={{
              '@container timer (min-width: 0px)': {
                display: 'grid',
              },
              '@container timer (min-width: 270px)': {
                display: 'none',
              },
            }}
          >
            {timer.slice(0, 2).map((t, index) => (
              <React.Fragment key={`ct-slot-${TIMER_SLOT_NAME[index]}`}>
                {index !== 0 && (
                  <GridItem>
                    <CountdownTimerText color={timerColor}>:</CountdownTimerText>
                  </GridItem>
                )}
                <GridItem>
                  <TimeSlot
                    key={index}
                    name={TIMER_SLOT_NAME[index]}
                    number={t}
                    labelColor={labelColor}
                    timerColor={timerColor}
                  />
                </GridItem>
              </React.Fragment>
            ))}
            {timer.slice(2).map((t, index) => (
              <React.Fragment key={`ct-slot-${TIMER_SLOT_NAME[index + 2]}`}>
                {index !== 0 && (
                  <GridItem>
                    <CountdownTimerText color={timerColor}>:</CountdownTimerText>
                  </GridItem>
                )}
                <GridItem>
                  <TimeSlot
                    key={index}
                    name={TIMER_SLOT_NAME[index + 2]}
                    number={t}
                    labelColor={labelColor}
                    timerColor={timerColor}
                  />
                </GridItem>
              </React.Fragment>
            ))}
          </Grid>
        </>
      )}
      {variant === CountdownTimerVariation.Compact && (
        <CountdownTimerText color={timerColor}>
          {timer
            .map((t, index) =>
              t === 0 || !timeSlots.includes(TIMER_SLOT_NAME[index].toLowerCase())
                ? null
                : `${t}${timeSlots.length > 1 ? TIMER_SLOT_NAME[index][0].toLowerCase() : ''}`,
            )
            .join(' ')}
          {timer.every((t) => t <= 0) && styles?.fallbackText ? styles.fallbackText : ''}
        </CountdownTimerText>
      )}
    </Box>
  )
}

export default CountdownTimer
